import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { useApp, AppContext } from '../../../AppContext';
import CustomSelectBox from '../../SearchSelecter';
import DynamicSimpleSearch from "../../DynamicSimpleSearch";
import DynamicSearch from '../../DynamicSearch';
import MobileBoardTable from './BoardTable/MobileBoardTable';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faChevronDown, faChevronUp, faPaperclip, faCheck, faEye, faHeart, faComment, faShare, faBookmark } from "@fortawesome/free-solid-svg-icons";

const BoardContainer = styled.div`
  width: 100%;
  max-width: 100vw;
  margin: 0 auto;
  padding: 0.5rem;
  background: #f5f5f5;
  min-height: 100vh;
  box-sizing: border-box;
  overflow-x: hidden;
`;

// 깔끔하고 모던한 모바일 검색 컨테이너
const MobileSimpleContainer = styled.div`
  width: 100%;
  max-width: 100%;
  background: white;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const MobileToggleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  cursor: pointer;
  background: #f8f9fa;
  border-bottom: ${props => props.isOpen ? '1px solid #e1e5e9' : 'none'};
  transition: all 0.2s ease;
  min-height: 50px;

  &:hover {
    background: #e9ecef;
  }

  &:active {
    background: #dee2e6;
  }
`;

const MobileToggleTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #495057;
  font-weight: 600;
  font-size: 0.9rem;
`;

const MobileToggleIcon = styled.div`
  color: #6c757d;
  font-size: 1rem;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const MobileSearchContent = styled.div`
  padding: ${props => props.isOpen ? '1rem' : '0'};
  max-height: ${props => props.isOpen ? '1000px' : '0'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  gap: 1rem;
  transition: all 0.3s ease;
  overflow: hidden;
  background: white;
`;

const MobileRow = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const MobileBox = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
`;

const MobileLabel = styled.label`
  font-size: 0.8rem;
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
`;

const MobileDateInput = styled(DatePicker)`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: white;
  color: #495057;
  font-size: 0.9rem;
  box-sizing: border-box;
  
  &::placeholder {
    color: #6c757d;
  }
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const MobileSelectHeader = styled.div`
  border: 1px solid #ced4da;
  padding: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  background: white;
  color: #495057;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  
  &:hover {
    border-color: #007bff;
  }
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const MobileSearchButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.875rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    background: #0056b3;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
  }
  
  &:active {
    transform: translateY(0);
    background: #004085;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.5);
  }
`;



const MobileDynamicBoard = (props) => {
  const { tableName } = props;
  const { user, logout } = useApp();

  // 데이터 관련 상태들 (데스크탑과 동일하게)
  const [posts, setPosts] = useState([]); // 데스크탑에서는 posts 사용
  const [boards, setBoards] = useState([]); // 호환성을 위해 유지
  const [columns, setColumns] = useState([]);
  const [searchParams, setSearchParams] = useState({});
  const [formFields, setFormFields] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(20); // 모바일 페이징 단위
  const [totalPosts, setTotalPosts] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [orderBy, setOrderBy] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [codeDisplayMap, setCodeDisplayMap] = useState({});
  const [hasTitleField, setHasTitleField] = useState(false);
  const [hasContentField, setHasContentField] = useState(false);
  const [values, setValues] = useState({}); // ManageBoard에서 사용하는 values 상태 추가
  const [menuConfig, setMenuConfig] = useState({
    use_editer: false,
    use_new: false,
    use_modify: false,
    use_delete: false,
    use_excel: false,
    use_trigger: false,
    use_view: false,
    use_upload: false,
    display_title: 'title',
    display_content: 'content',
    sort_order: 'desc',
    post_btn_type: false,
    search_type: false,
  });
  
  // MobileSimpleSearchForm 관련 상태들
  const [selectedField, setSelectedField] = useState('');
  const [options, setOptions] = useState([]);
  const [arraySelectOptions, setArraySelectOptions] = useState([]);
  const [formData, setFormData] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  
  // 검색 관련 함수들
  const handleSearchChange = (field, value) => {
    console.log('Mobile search change:', field, value);
    setSearchParams(prev => ({ ...prev, [field]: value }));
  };

  // checkSession 함수 추가 (데스크탑과 동일)
  const checkSession = async () => {
    try {
      const response = await fetch('/request/checkSession', {
        credentials: 'include',
        method: 'GET'
      });
      return response.ok;
    } catch (error) {
      console.error('Session check failed:', error);
      return false;
    }
  };

  // 데이터 로딩 함수 (데스크탑과 완전히 동일)
  const getBoardData = async (page = 1, limit = 20, table, searchParams = {}, orderByColumns, callName) => {
    if (!await checkSession()) {
      console.log('Mobile: Session check failed, aborting data fetch');
      return;
    }
    
    try {
      console.log('Mobile getBoardData called with:', { page, limit, table, searchParams, orderByColumns });
      
      const adjustedSearchParams = { ...searchParams };

      // 'from'이 존재하고 값이 유효하면 추가
      if (searchParams?.from) {
        adjustedSearchParams.from = searchParams.from;
      }
      
      if (searchParams?.to) {
        adjustedSearchParams.to = searchParams.to;
      }
      
      const filteredParams = Object.entries(adjustedSearchParams)
        .filter(([key, value]) => value !== undefined && value !== null && value !== '')
        .reduce((obj, [key, value]) => {
          obj[key] = value;
          return obj;
        }, {});
        
      const queryParams = new URLSearchParams({
        page,
        limit,
        table: table || tableName, // table 파라미터 확실히 설정
        ...filteredParams,
        orderby: Array.isArray(orderByColumns) && orderByColumns.length > 0 ? orderByColumns.join(',') : '',
        sort: menuConfig.sort_order,
      }).toString();

      const fullUrl = `/request/dynamicboardList?${queryParams}`;
      console.log('🌐 Mobile API URL:', fullUrl);
      console.log('🌐 Mobile query params:', queryParams);

      const response = await fetch(fullUrl, {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      console.log('📊 Mobile board data received:', data);
      
      // 데스크탑과 정확히 동일한 데이터 매핑
      const posts = data.boardData[0].sampleRows || [];
      const columns = data.boardData[0].columns || [];
      const total = data.total || 0;
      
      console.log('📊 Mapped posts:', posts);
      console.log('📊 Mapped columns:', columns);
      console.log('📊 Mapped total:', total);
      
      // 항상 데이터를 교체 (페이지네이션 방식)
      setPosts(posts);
      setBoards(posts); // 호환성을 위해 boards도 설정
      setTotalPosts(total);
      
      if (columns && columns.length > 0) {
        setColumns(columns);
        setFormFields(columns.filter(col => col.col_search));
        // title, content 필드 확인
        setHasTitleField(columns.some(col => col.column_id === 'title'));
        setHasContentField(columns.some(col => col.column_id === 'content'));
      }
      
      // menuConfig 설정
      const boardData = data.boardData[0];
      setMenuConfig({
        use_new: boardData.use_new,
        use_editer: boardData.use_editer,
        use_modify: boardData.use_modify,
        use_delete: boardData.use_delete,
        use_excel: boardData.use_excel,
        use_trigger: boardData.use_trigger,
        use_view: boardData.use_view,
        use_upload: boardData.use_upload,
        display_title: boardData.display_title,
        display_content: boardData.display_content,
        sort_order: boardData.sort_order,
        post_btn_type: boardData.post_btn_type,
        search_type: boardData.search_type,
      });
      
      if (data.codeDisplayMap) {
        setCodeDisplayMap(data.codeDisplayMap);
      }
      
    } catch (error) {
      console.error('Error fetching mobile board data:', error);
    }
  };

  // 데스크탑과 동일한 초기화 로직
  const fetchBoardData = async (searchParams, currentPage) => {
    console.log('🚀 fetchBoardData started');
    try {
      // 액션 정보 가져오기
      console.log('📡 Calling getActionInfo API...');
      const response = await fetch(`/request/getActionInfo?board_id=${tableName}`);
      const data = await response.json();
      console.log('📡 getActionInfo response:', data);
      
      // 보드 데이터 가져오기
      console.log('📡 Calling getBoardData...');
      await getBoardData(
        currentPage,
        postsPerPage,
        tableName,
        searchParams,
        orderBy,
        'main data fetch'
      );
    } catch (error) {
      console.error('❌ Error in fetchBoardData:', error);
    }
  };

  // 초기 데이터 로딩
  useEffect(() => {
    console.log('=== MobileDynamicBoard useEffect ===');
    console.log('tableName:', tableName);
    console.log('user:', user);
    console.log('currentPage:', currentPage);
    console.log('postsPerPage:', postsPerPage);
    
    if (tableName && user) {
      console.log('✅ Calling fetchBoardData with:', { currentPage, postsPerPage, tableName, searchParams, orderBy });
      fetchBoardData(searchParams, currentPage);
    } else {
      console.log('❌ Not calling fetchBoardData - tableName:', !!tableName, 'user:', !!user);
    }
  }, [tableName, currentPage, refreshFlag, user]);

  // 검색 시 데이터 다시 로딩
  useEffect(() => {
    if (tableName && Object.keys(searchParams).length > 0) {
      getBoardData(1, postsPerPage, tableName, searchParams, orderBy);
      setCurrentPage(1);
    }
  }, [searchParams]);

  // MobileSimpleSearchForm 관련 useEffect들
  useEffect(() => {
    setSelectedField('');
    setOptions([]);
    setArraySelectOptions([]);
    setFormData({});
  }, [formFields]);

  useEffect(() => {
    if (selectedField) {
      const field = formFields.find(f => f.column_id === selectedField);
      fetchOptions(field);
      if (field?.control_type === 'ArrayInput') {
        fetchArrayOptions(field);
      }
    }
  }, [selectedField]);

  // MobileSimpleSearchForm 관련 함수들
  const fetchOptions = async (field) => {
    if (!field) return;
    let url;
    let response;

    if (field?.control_type === 'SelectBox' && field.bind_key) {
      try {
        url = `/request/getFormControlOptions?bind_key=${field.bind_key}&bind_option=${field.bind_option}`;
        response = await fetch(url, { credentials: 'include', headers: { Accept: 'application/json' }, method: 'GET' });
        if (response.ok) {
          const data = await response.json();
          setOptions(data);
        }
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    }
  };

  const fetchArrayOptions = async (field) => {
    if (!field) return;
    try {
      const response = await fetch(`/request/getOptions?target_code=${field.root_column}&code_group=${field.bind_key}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const formattedOptions = data.options.map((item) => ({
          value: item.code_value,
          label: `[ ${item.code_option} | ${item.code_value} ]`,
        }));
        setArraySelectOptions(formattedOptions);
      }
    } catch (error) {
      console.error("Error fetching array options:", error);
    }
  };

  const handleFieldChange = (event) => {
    const newField = event.target.value;
    setSelectedField(newField);
    setFormData(prevFormData => ({
      ...prevFormData,
      [newField]: '',
    }));
  };

  const handleInputChange = (fieldId, value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldId]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    let Params = { ...searchParams, [selectedField]: formData[selectedField] };
    setSearchParams(Params);
    getBoardData(1, postsPerPage, tableName, Params, orderBy, 'SimpleSearchForm');
    setCurrentPage(1);
  };

  // 페이지 변경 함수
  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);
    await getBoardData(newPage, postsPerPage, tableName, searchParams, orderBy, 'PageChange');
  };

  return (
    <BoardContainer>
      
      {/* 토글 기능이 있는 모바일 검색 폼 */}
      <MobileSimpleContainer>
        <MobileToggleHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
          <MobileToggleTitle>
            <FontAwesomeIcon icon={faSearch} />
            심플 검색
          </MobileToggleTitle>
          <MobileToggleIcon isOpen={isOpen}>
            <FontAwesomeIcon icon={faChevronDown} />
          </MobileToggleIcon>
        </MobileToggleHeader>

        <MobileSearchContent isOpen={isOpen}>
          <MobileRow>
            <MobileBox>
              <MobileLabel>시작일:</MobileLabel>
              <MobileDateInput
                selected={searchParams?.from}
                onChange={(date) => handleSearchChange('from', date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="시작 날짜"
              />
            </MobileBox>
            <MobileBox>
              <MobileLabel>종료일:</MobileLabel>
              <MobileDateInput
                selected={searchParams?.to}
                onChange={(date) => handleSearchChange('to', date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="종료 날짜"
              />
            </MobileBox>
          </MobileRow>

          <MobileBox>
            <MobileLabel>검색 조건:</MobileLabel>
            <CustomSelectBox
              value={selectedField}
              onChange={handleFieldChange}
              isSearch={false}
              isCancel={true}
              placeholder="검색조건을 선택하세요"
              SelectHeader={MobileSelectHeader}
              options={formFields
                .filter((field) => field?.col_search)
                .map((field) => ({
                  std_value: field?.column_id,
                  std_key: field?.column_title,
                }))}
            />
          </MobileBox>

          {selectedField && (
            <MobileBox>
              <MobileLabel>검색 값:</MobileLabel>
              <DynamicSimpleSearch
                field={formFields.find((field) => field.column_id === selectedField)}
                value={formData[selectedField] || ''}
                onChange={handleInputChange}
                isLable={false}
              />
            </MobileBox>
          )}

          <MobileSearchButton type="submit" onClick={handleSubmit}>
            🔍 검색
          </MobileSearchButton>
        </MobileSearchContent>
      </MobileSimpleContainer>
      
      

      {/* 모바일 전용 BoardTable 사용 */}
      <MobileBoardTable
        boards={posts}
        columns={columns}
        selectedRows={selectedRows}
        toggleRowSelection={(index) => {
          setSelectedRows(prev => 
            prev.includes(index) 
              ? prev.filter(i => i !== index)
              : [...prev, index]
          );
        }}
        isEditMode={false}
        editedRows={{}}
        handleRowChange={() => {}}
        setIsOpenViewer={() => {}}
        setOpenPost={() => {}}
        codeDisplayMap={codeDisplayMap}
        isAllSelected={selectedRows.length === posts.length && posts.length > 0}
        setIsAllSelected={(isSelected) => {
          if (isSelected) {
            setSelectedRows(posts.map((_, index) => index));
          } else {
            setSelectedRows([]);
          }
        }}
        tableName={tableName}
        onRefresh={() => setRefreshFlag(prev => prev + 1)}
        setSelectedRows={setSelectedRows}
        setIsShowDynamicPopup={() => {}}
        isOpenViewer={false}
        refreshFlag={refreshFlag}
        setRefreshFlag={setRefreshFlag}
        searchParams={searchParams}
        setPopupType={() => {}}
        saveMessage=""
        values={values}
        setValues={setValues}
        menuConfig={menuConfig}
        setMenuConfig={setMenuConfig}
        // 서버 페이징 관련 props 추가
        totalPosts={totalPosts}
        currentPage={currentPage}
        postsPerPage={postsPerPage}
        onPageChange={handlePageChange}
      />
    </BoardContainer>
  );
};

export default MobileDynamicBoard;